// @ts-strict-ignore
import { createReducer, on } from '@ngrx/store';
import * as ScheduledReportsActions from '../actions/scheduled-reports.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ServerError } from '../../reports/components/schedule-report/schedule-report-form.component';

export const scheduledReportsFeatureKey = 'scheduledReports';

export interface State extends EntityState<ScheduleReport> {
  selectedReportType: ReportType;
  loading: boolean;
  error: ServerError;
  filters: { type: string; name: string }[];
}

export type ReportType =
  | 'report_ifta'
  | 'report_vehicle_usage'
  | 'report_vehicle_odometer'
  | 'report_vehicle_work_hours'
  | 'report_maintenance'
  | 'report_unresponsive'
  | 'report_location'
  | 'report_vehicle_state'
  | 'report_engine_hours_by_location'
  | 'report_location_total_time'
  | 'all';

export interface ScheduleReport {
  id?: number;
  vehicleGroup: number;
  enabled: boolean;
  reportType: ReportType;
  period: string;
  scheduledTime: string;
  scheduledWeekday: string;
}

export const adapter: EntityAdapter<ScheduleReport> =
  createEntityAdapter<ScheduleReport>();

export const initialState: State = adapter.getInitialState({
  selectedReportType: 'all',
  loading: false,
  error: null,
  filters: [],
});

export const reducer = createReducer(
  initialState,

  on(
    ScheduledReportsActions.loadScheduledReports,
    ScheduledReportsActions.loadScheduledReports,
    ScheduledReportsActions.addReportSchedule,
    ScheduledReportsActions.updateSchedule,
    (state) => ({
      ...state,
      loading: true,
      error: null as ServerError,
    }),
  ),
  on(ScheduledReportsActions.loadScheduledReportsSuccess, (state, action) => ({
    ...adapter.setAll(action.scheduleList, state),
    loading: false,
  })),
  on(ScheduledReportsActions.addReportScheduleSuccess, (state, action) => ({
    ...adapter.addOne(action.schedule, state),
    loading: false,
  })),
  on(ScheduledReportsActions.deleteScheduleReportSuccess, (state, action) => ({
    ...adapter.removeOne(action.id, state),
    loading: false,
  })),
  on(ScheduledReportsActions.resetScheduledReportsFilter, (state) => ({
    ...state,
    filters: [] as { type: string; name: string }[],
  })),
  on(ScheduledReportsActions.setScheduleFilter, (state, action) => ({
    ...state,
    filters: state.filters
      .map((i) => i.name)
      .includes(action.scheduleFilter.name)
      ? state.filters.filter(
          (filterItem) => filterItem.name !== action.scheduleFilter.name,
        )
      : [...state.filters, action.scheduleFilter],
  })),
  on(ScheduledReportsActions.updateScheduleSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        id: action.schedule.id,
        changes: action.schedule,
      },
      state,
    ),
    loading: false,
  })),
  on(ScheduledReportsActions.loadScheduledReportsFailure, (state, action) => ({
    ...state,
    error: action.error,
    loading: false,
  })),
  on(ScheduledReportsActions.clearScheduleReportError, (state) => ({
    ...state,
    error: null as ServerError,
  })),
  on(ScheduledReportsActions.setSelectedReportType, (state, action) => ({
    ...state,
    selectedReportType: action.reportType,
  })),
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const getScheduledReportsLoading = (state: State) => state.loading;
export const getScheduleReportError = (state: State) => state.error;
export const getSelectedReportType = (state: State) => state.selectedReportType;
export const getScheduleFilters = (state: State) => state.filters;
